import { useTranslate } from "@refinedev/core";
import { Flex, Typography } from "antd";
import styled from "styled-components";
import { useTeacherOnboarding } from "./teacherOnboardingContext";
import { useOnboardingQuestionnaire } from "../creator-profile/hooks/useOnboardingQuestionnaire";
import { I18N_PREFIX, TITLE_FONT } from "./constants";
import type { OnboardingTeacherQuestionnaire } from "../creator-profile/types";
import type { TeacherStep } from "./types";

// The template question whose English text is the fallback for a given step.
type TemplateQuestionField = Exclude<
  keyof OnboardingTeacherQuestionnaire,
  "type"
>;

const STEP_TEMPLATE_FIELD: Partial<Record<TeacherStep, TemplateQuestionField>> =
  {
    voice1: "voice1",
    voice2: "voice2",
    illustration: "illustration",
    structure: "structure",
    depth: "depth",
    autonomy: "autonomy",
    guardrail: "guardrail",
  };

const StepHeader = ({ i18nBase }: { i18nBase: string }) => {
  const t = useTranslate();
  const { currentStep, stepNumber } = useTeacherOnboarding();
  // GET is de-duplicated by React Query, so calling this per step is cheap.
  const { template: questionnaire } = useOnboardingQuestionnaire("teacher");
  const template =
    questionnaire?.type === "teacher" ? questionnaire : undefined;

  // Wording stays i18n; only when the translation key is missing do we fall
  // back to the API's (English) question text.
  const questionKey = `${I18N_PREFIX}.${i18nBase}.question`;
  const translated = t(questionKey);
  const field = STEP_TEMPLATE_FIELD[currentStep];
  const apiQuestion = field ? template?.[field]?.question : undefined;
  const title =
    translated === questionKey && apiQuestion ? apiQuestion : translated;

  return (
    <Flex
      vertical
      gap={6}
      align="start"
      style={{ width: "100%", maxWidth: 560, textAlign: "left" }}
    >
      <Kicker>
        {t(`${I18N_PREFIX}.stepLabel`)} {stepNumber} ·{" "}
        {t(`${I18N_PREFIX}.${i18nBase}.kicker`)}
      </Kicker>
      <Typography.Title level={3} style={{ margin: 0, fontFamily: TITLE_FONT }}>
        {title}
      </Typography.Title>
    </Flex>
  );
};

export default StepHeader;

// ─── Styled components ───

const Kicker = styled.div`
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  font-weight: 700;
  color: ${({ theme }) => theme.colorPrimary};
`;
